// File: src/pages/DeleteAccountFlow.jsx

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  LuArrowLeft,
  LuCircleAlert,
  LuTrash2,
} from 'react-icons/lu';
import { ReactComponent as TribeLogo } from '../assets/TribeLogo.svg';
import EventHelpTooltip from '../components/EventPreview/EventHelpTooltip';

const fontBase =
  "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif";

const C = {
  bg: '#ffffff',
  text: '#0b0b0b',
  sub: '#6b7280',
  border: '#e5e7eb',
  borderStrong: '#eef0f2',
  black: '#000000',
};

function Card({ tone = 'neutral', style, children }) {
  const tones = {
    neutral: {
      border: C.borderStrong,
      background: C.bg,
    },
    warning: {
      border: '#FEE2E2',
      background: '#FEF2F2',
    },
  };

  const selectedTone = tones[tone] || tones.neutral;

  return (
    <div
      style={{
        border: `1px solid ${selectedTone.border}`,
        background: selectedTone.background,
        borderRadius: 12,
        padding: 16,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export default function DeleteAccountFlow() {
  const navigate = useNavigate();
  const { userId } = useParams();

  const [email, setEmail] = useState('');
  const [typedText, setTypedText] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const deleteLockRef = useRef(false);

  useEffect(() => {
    async function fetchUser() {
      try {
        if (!userId) return;

        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/users/${userId}`
        );

        setEmail(res.data.email || '');
      } catch (err) {
        console.error(
          'Failed to fetch user email =>',
          err
        );
      }
    }

    fetchUser();
  }, [userId]);

  const canSubmit =
    typedText.trim().toUpperCase() === 'DELETE' &&
    !isLoading;

  function handleBack() {
    if (isLoading) return;
    navigate('/settings');
  }

  function handleCancel() {
    if (isLoading) return;
    navigate('/settings');
  }

  async function handleConfirmDelete() {
    if (
      !canSubmit ||
      isLoading ||
      deleteLockRef.current
    ) {
      return;
    }

    deleteLockRef.current = true;
    setIsLoading(true);

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/users/${userId}`
      );

      localStorage.removeItem('userId');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('sessionId');
      localStorage.removeItem('jwt');
      localStorage.removeItem('tribe_auth');

      alert('Account deleted successfully!');

      navigate('/login', {
        replace: true,
      });
    } catch (err) {
      console.error(
        'Failed to delete account =>',
        err
      );

      alert(
        err.response?.data?.message ||
          err.message ||
          'Failed to delete account.'
      );

      deleteLockRef.current = false;
      setIsLoading(false);
    }
  }

  return (
    <div style={S.page}>
      <header style={S.header}>
        <div style={S.logoRow}>
          <TribeLogo style={S.logo} />
        </div>

        <div style={S.navRow}>
          <button
            type="button"
            onClick={handleBack}
            disabled={isLoading}
            style={{
              ...S.backBtn,
              opacity: isLoading ? 0.6 : 1,
              cursor: isLoading
                ? 'not-allowed'
                : 'pointer',
            }}
            aria-label="Back"
          >
            <LuArrowLeft
              size={16}
              color="#fff"
            />
          </button>

          <div style={S.title}>
            Delete Account
          </div>

          <div style={S.rightSlot}>
            <EventHelpTooltip
              title="About Account Deletion"
              body="Deleting your account permanently removes your profile, settings, connections, messages, and event history. This action cannot be undone."
            />
          </div>
        </div>
      </header>

      <main style={S.container}>
        <section style={S.hero}>
          <div style={S.iconCircle}>
            <LuTrash2
              style={{
                fontSize: '1.5rem',
                color: '#000',
                display: 'block',
              }}
            />
          </div>

          <h2 style={S.h2Center}>
            Delete your account
          </h2>

          <p style={S.subCenter}>
            You’re about to permanently remove
            the account tied to
            <br />

            <span style={S.inlineTag}>
              {email || '…'}
            </span>
          </p>
        </section>

        <Card
          tone="warning"
          style={{
            marginTop: 8,
          }}
        >
          <div style={S.warningRow}>
            <LuCircleAlert
              size={18}
              strokeWidth={2.4}
              style={S.warningIcon}
            />

            <div>
              <div style={S.warningTitle}>
                This can’t be undone
              </div>

              <ul style={S.list}>
                <li>
                  Your profile and account settings
                  will be permanently removed.
                </li>

                <li>
                  Your connections, history, and
                  messages will be deleted.
                </li>

                <li>
                  You’ll lose access to current and
                  future gatherings.
                </li>
              </ul>
            </div>
          </div>
        </Card>

        <section style={S.formFrame}>
          <div style={S.group}>
            <label
              htmlFor="delete-text"
              style={S.label}
            >
              Type <strong>DELETE</strong> to
              confirm
            </label>

            <input
              id="delete-text"
              type="text"
              placeholder="DELETE"
              autoComplete="off"
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              disabled={isLoading}
              value={typedText}
              onChange={(event) => {
                setTypedText(
                  event.target.value.toUpperCase()
                );
              }}
              onKeyDown={(event) => {
                if (
                  event.key === 'Enter' &&
                  canSubmit
                ) {
                  handleConfirmDelete();
                }
              }}
              style={{
                ...S.input,
                opacity: isLoading ? 0.65 : 1,
                cursor: isLoading
                  ? 'not-allowed'
                  : 'text',
              }}
            />
          </div>
        </section>
      </main>

      <div
        style={{
          ...S.footerBar,
          paddingBottom:
            'calc(12px + env(safe-area-inset-bottom, 0px))',
        }}
      >
        <button
          type="button"
          onClick={handleCancel}
          disabled={isLoading}
          style={{
            ...S.secondaryBtn,
            opacity: isLoading ? 0.6 : 1,
            cursor: isLoading
              ? 'not-allowed'
              : 'pointer',
          }}
        >
          Cancel
        </button>

        <button
          type="button"
          onClick={handleConfirmDelete}
          disabled={!canSubmit}
          style={{
            ...S.primaryBtnDestructive,
            ...(!canSubmit
              ? S.primaryBtnDestructiveDisabled
              : {}),
          }}
        >
          {isLoading
            ? 'Deleting…'
            : 'Confirm deletion'}
        </button>
      </div>
    </div>
  );
}

const S = {
  page: {
    fontFamily: fontBase,
    background: C.bg,
    color: C.text,
    minHeight: '100vh',
  },

  header: {
    display: 'flex',
    flexDirection: 'column',
    background: C.bg,
    position: 'sticky',
    top: 0,
    zIndex: 100,
  },

  logoRow: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: '0.5rem',
  },

  logo: {
    width: 72,
    height: 'auto',
  },

  navRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    height: 44,
    padding: '0.5rem 1rem',
  },

  backBtn: {
    position: 'absolute',
    left: 16,
    background: C.black,
    color: '#fff',
    borderRadius: 9999,
    width: 40,
    height: 28,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 0,
    padding: 0,
    border: 'none',
  },

  title: {
    fontSize: '0.95rem',
    fontWeight: 850,
    lineHeight: 1,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: '#0b0b0b',
    width: '100%',
    textAlign: 'center',
    zIndex: 1000,
  },

  rightSlot: {
    position: 'absolute',
    right: 16,
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'flex',
    alignItems: 'center',
  },

  container: {
  width: '100%',
  maxWidth: 544,
  margin: '0 auto',
  padding:
    '1rem 1.5rem calc(110px + env(safe-area-inset-bottom, 0px))',
  boxSizing: 'border-box',
},

  hero: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    margin: '4px 0 8px',
  },

  iconCircle: {
    width: 50,
    height: 50,
    borderRadius: 12,
    background: '#f3f4f6',
    display: 'grid',
    placeItems: 'center',
    margin: '0 auto 0px',
  },

  h2Center: {
    fontSize: '1.35rem',
    fontWeight: 850,
    color: '#0b0b0b',
    margin: '12px 0 8px',
    letterSpacing: '-0.035em',
    lineHeight: 1.05,
    textAlign: 'center',
  },

  subCenter: {
    fontSize: '0.92rem',
    color: 'rgba(17,17,17,0.58)',
    margin: '0 0 1.35rem 0',
    lineHeight: 1.48,
    fontWeight: 500,
    letterSpacing: '-0.01em',
    textAlign: 'center',
  },

  warningRow: {
    display: 'flex',
    gap: 10,
    alignItems: 'flex-start',
  },

  warningIcon: {
    color: '#B91C1C',
    marginTop: 1,
    flexShrink: 0,
  },

  warningTitle: {
    fontWeight: 850,
    fontSize: 13,
    color: C.text,
    marginBottom: 6,
    letterSpacing: '-0.015em',
  },

  formFrame: {
    marginTop: 18,
    borderRadius: 12,
    padding: 0,
    background: C.bg,
  },

  group: {
    marginTop: 0,
  },

  label: {
    display: 'block',
    fontSize: 12,
    fontWeight: 700,
    color: '#111827',
    marginBottom: 8,
    textAlign: 'left',
  },

 input: {
  width: '100%',
  height: 42,
  padding: '0 14px',
  fontSize: 12,
  lineHeight: 1.35,
  border: `1px solid ${C.border}`,
  borderRadius: 10,
  outline: 'none',
  background: '#fff',
  color: C.text,
  boxSizing: 'border-box',
  fontFamily: fontBase,
  fontWeight: 650,
  letterSpacing: '0.04em',
},

  list: {
    paddingLeft: '1.1rem',
    margin: 0,
    display: 'grid',
    gap: 4,
    color: C.sub,
    fontSize: 13,
    lineHeight: 1.45,
    paddingBottom: 2,
  },

  inlineTag: {
    display: 'inline-block',
    background: '#f3f4f6',
    color: C.text,
    fontWeight: 800,
    padding: '2px 8px',
    borderRadius: 6,
    marginTop: 10,
    letterSpacing: '-0.01em',
  },

  footerBar: {
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: 0,
    background: C.bg,
    borderTop: `1px solid ${C.border}`,
    padding: '10px 12px',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 8,
    zIndex: 999,
  },

  primaryBtnDestructive: {
    width: '100%',
    height: 42,
    background: '#B91C1C',
    color: '#fff',
    border: '1px solid #B91C1C',
    borderRadius: 8,
    padding: '0 13px',
    fontSize: 12,
    fontWeight: 875,
    letterSpacing: '-0.015em',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },

  primaryBtnDestructiveDisabled: {
  background: '#fca5a5',
  border: '1px solid #fca5a5',
  cursor: 'not-allowed',
},
  secondaryBtn: {
    width: '100%',
    height: 42,
    background: '#fff',
    color: C.text,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    padding: '0 13px',
    fontSize: 12,
    fontWeight: 875,
    letterSpacing: '-0.015em',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
};